import React, { useState } from 'react';
import {
    Box,
    Modal,
    IconButton,
    Typography,
    Backdrop,
    Fade,
    Chip,
    Zoom
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import TouchAppIcon from '@mui/icons-material/TouchApp';

const ImageViewer = ({ src, alt = 'Question Diagram' }) => {
    const [open, setOpen] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [imageError, setImageError] = useState(false);

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setZoomLevel(1);
        setPosition({ x: 0, y: 0 });
    };

    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 0.5, 4));
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 0.5, 0.5));
    };

    const handleReset = () => {
        setZoomLevel(1);
        setPosition({ x: 0, y: 0 });
    };

    const handleMouseDown = (e) => {
        if (zoomLevel > 1) {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - position.x,
                y: e.clientY - position.y
            });
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging) {
            setPosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleWheel = (e) => {
        e.preventDefault();
        if (e.deltaY < 0) {
            handleZoomIn();
        } else {
            handleZoomOut();
        }
    };

    const handleImageError = () => {
        setImageError(true);
    };

    if (!src) return null;

    return (
        <>
            <Box
                sx={{
                    textAlign: 'center',
                    mb: 4,
                    position: 'relative',
                    display: 'inline-block',
                    width: '100%',
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        display: 'inline-block',
                        maxWidth: '100%',
                        cursor: 'pointer',
                        '&:hover': {
                            '& .hover-overlay': {
                                opacity: 1,
                            },
                            '& img': {
                                transform: 'scale(1.02)',
                                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                            }
                        }
                    }}
                    onClick={handleOpen}
                >
                    <img
                        src={src}
                        alt={alt}
                        onError={handleImageError}
                        style={{
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: '12px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            display: imageError ? 'none' : 'block',
                        }}
                    />

                    {/* Hover Overlay */}
                    <Box
                        className="hover-overlay"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                            borderRadius: '12px',
                            pointerEvents: 'none',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <FullscreenIcon
                                sx={{
                                    color: 'white',
                                    fontSize: 48,
                                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'white',
                                    fontWeight: 600,
                                    letterSpacing: '0.5px',
                                    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                }}
                            >
                                Click to Enlarge
                            </Typography>
                        </Box>
                    </Box>

                    {/* Click to Enlarge Chip */}
                    <Chip
                        icon={<TouchAppIcon />}
                        label="Click image to enlarge"
                        size="small"
                        sx={{
                            position: 'absolute',
                            bottom: 12,
                            right: 12,
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            '& .MuiChip-icon': {
                                fontSize: '1rem',
                                color: '#ff5e1c',
                            },
                        }}
                    />
                </Box>

                {imageError && (
                    <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                        Unable to load image
                    </Typography>
                )}
            </Box>

            {/* Modal for Enlarged Image */}
            <Modal
                open={open}
                onClose={handleClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.95)',
                    }
                }}
            >
                <Fade in={open}>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '90vw',
                            height: '90vh',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            outline: 'none',
                        }}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                    >
                        {/* Control Bar */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 20,
                                right: 20,
                                display: 'flex',
                                gap: 1,
                                zIndex: 10,
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '12px',
                                padding: '8px',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                            }}
                        >
                            <IconButton
                                onClick={handleZoomIn}
                                sx={{
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    }
                                }}
                                disabled={zoomLevel >= 4}
                            >
                                <ZoomInIcon />
                            </IconButton>
                            <IconButton
                                onClick={handleZoomOut}
                                sx={{
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    }
                                }}
                                disabled={zoomLevel <= 0.5}
                            >
                                <ZoomOutIcon />
                            </IconButton>
                            <IconButton
                                onClick={handleReset}
                                sx={{
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    }
                                }}
                            >
                                <RestartAltIcon />
                            </IconButton>
                            <IconButton
                                onClick={handleClose}
                                sx={{
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 94, 28, 0.8)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 94, 28, 1)',
                                    }
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        {/* Zoom Level Indicator */}
                        <Chip
                            label={`${Math.round(zoomLevel * 100)}%`}
                            sx={{
                                position: 'absolute',
                                bottom: 20,
                                right: 20,
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                backdropFilter: 'blur(10px)',
                                color: 'white',
                                fontWeight: 600,
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                            }}
                        />

                        {/* Instructions */}
                        <Typography
                            variant="caption"
                            sx={{
                                position: 'absolute',
                                bottom: 20,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                color: 'rgba(255, 255, 255, 0.7)',
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                padding: '8px 16px',
                                borderRadius: '8px',
                                backdropFilter: 'blur(10px)',
                            }}
                        >
                            Use mouse wheel to zoom • {zoomLevel > 1 ? 'Click and drag to pan' : 'Zoom in to enable panning'}
                        </Typography>

                        {/* Image Container */}
                        <Box
                            sx={{
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                            }}
                            onWheel={handleWheel}
                        >
                            <Zoom in={open} timeout={300}>
                                <img
                                    src={src}
                                    alt={alt}
                                    style={{
                                        maxWidth: zoomLevel > 1 ? 'none' : '90%',
                                        maxHeight: zoomLevel > 1 ? 'none' : '90%',
                                        width: zoomLevel > 1 ? `${zoomLevel * 100}%` : 'auto',
                                        height: 'auto',
                                        transform: `translate(${position.x}px, ${position.y}px)`,
                                        transition: isDragging ? 'none' : 'transform 0.3s ease',
                                        borderRadius: '8px',
                                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
                                        imageRendering: 'crisp-edges',
                                        WebkitImageRendering: 'crisp-edges',
                                        userSelect: 'none',
                                        pointerEvents: 'auto',
                                    }}
                                    onMouseDown={handleMouseDown}
                                    draggable={false}
                                />
                            </Zoom>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </>
    );
};

export default ImageViewer;